import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs, PluralMap } from '@trimble-gcs/i18n-types';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { catchError, filter, map, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { DialogComponent } from '../../dialog/dialog.component';
import { CANCEL_BUTTON, DialogData } from '../../dialog/dialog.model';
import { DialogService } from '../../dialog/dialog.service';
import { LoadingService } from '../../loading/loading.service';
import {
  ScandataEmptyComponent,
  ScandataEmptyReason,
} from '../../scandata-list/scandata-empty/scandata-empty.component';
import {
  ClearRecycledScans,
  SetPreviewVisible,
  SetSelectedRecycledScans,
  SetSortInfo,
} from '../../scandata/recycle/scandata-recycle.actions';
import { PERMANENT_DELETE_AFTER_DAYS } from '../../scandata/recycle/scandata-recycle.models';
import { ScandataRecycleService } from '../../scandata/recycle/scandata-recycle.service';
import { ScandataRecycleState } from '../../scandata/recycle/scandata-recycle.state';
import { SortInfo } from '../../scandata/scandata-query.models';
import { sortScandata } from '../../scandata/scandata-sort';
import { ScandataModel } from '../../scandata/scandata.models';
import { RecycleBinOptionsPanelComponent } from './recycle-bin-options-panel/recycle-bin-options-panel.component';
import { RecycleBinTableComponent } from './recycle-bin-table/recycle-bin-table.component';
import { recycleBinTranslations } from './recycle-bin.translations';

@UntilDestroy()
@Component({
  selector: 'sd-recycle-bin',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
    RecycleBinTableComponent,
    ScandataEmptyComponent,
    MatSidenavModule,
    RecycleBinOptionsPanelComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './recycle-bin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecycleBinComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly loadingService = inject(LoadingService);
  private readonly scandataRecycleService = inject(ScandataRecycleService);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);

  readonly translations = recycleBinTranslations();

  readonly isLoading = toSignal(this.loadingService.isLoading$(this), { initialValue: false });
  readonly sortInfo = this.store.selectSignal(ScandataRecycleState.sortInfo);
  readonly previewVisible = this.store.selectSignal(ScandataRecycleState.previewVisible);

  private readonly recycledScans = this.store.selectSignal(ScandataRecycleState.recycledScans);
  readonly chronologicalSelected = this.store.selectSignal(
    ScandataRecycleState.chronologicalSelected,
  );

  readonly recycledScansList = computed(() => {
    const recycledScans = this.recycledScans();
    const sort = this.sortInfo();

    return sortScandata(recycledScans, sort.sortBy, sort.sortDirection);
  });

  readonly selectedScans = computed(() => this.recycledScans().filter((scan) => scan.selected));
  readonly showOptionsPanel = computed(() => this.selectedScans().length > 0);

  readonly disableActions = computed(() => {
    return this.isLoading() || this.selectedScans().length === 0;
  });
  readonly disableSelection = computed(() => this.isLoading());

  readonly showEmpty = computed(() => {
    return this.isLoading() ? false : this.recycledScans().length === 0;
  });
  readonly emptyReason = ScandataEmptyReason.NoRecycled;

  readonly autoDeleteText = computed(() => {
    const pluralArgs: PluralArgs = { count: PERMANENT_DELETE_AFTER_DAYS };
    const pluralMap: PluralMap = this.translations.autoDelete;
    return this.translationService.translatePlural(pluralArgs, pluralMap);
  });

  readonly loadError = signal(false);

  private readonly loadScans$ = new Subject<void>();

  private confirmDeleteDialogRef: MatDialogRef<DialogComponent> | null = null;
  private deleteErrorDialogRef: MatDialogRef<DialogComponent> | null = null;
  private restoreErrorDialogRef: MatDialogRef<DialogComponent> | null = null;

  constructor() {
    this.subscribeToLoadRecycledScans();
  }

  ngOnDestroy(): void {
    this.store.dispatch(ClearRecycledScans);

    this.confirmDeleteDialogRef?.close();
    this.deleteErrorDialogRef?.close();
    this.restoreErrorDialogRef?.close();
  }

  previewVisibleChanged(previewVisible: boolean) {
    this.store.dispatch(new SetPreviewVisible(previewVisible));
  }

  optionsCloseClicked() {
    const scans = this.recycledScans().map((scan) => ({ ...scan, selected: false }));
    this.store.dispatch(new SetSelectedRecycledScans(scans));
  }

  selectionChange(scans: ScandataModel[]) {
    this.store.dispatch(new SetSelectedRecycledScans(scans));
  }

  sortInfoChange(sortInfo: SortInfo) {
    this.store.dispatch(new SetSortInfo(sortInfo));
  }

  refreshClick() {
    this.loadScans$.next();
  }

  restoreScans() {
    const restoreScans = this.selectedScans();
    const restoreScans$ = this.scandataRecycleService.restoreScans(restoreScans);

    this.loadingService.loadFrom(restoreScans$, this).subscribe(({ failed }) => {
      if (failed.length === 0) return;

      this.showRestoreErrors(failed);
    });
  }

  deleteScansPermanently() {
    const deleteScans = this.selectedScans();

    this.showDeleteConfirmation(deleteScans)
      .pipe(
        switchMap((scans) => {
          const deleteScans$ = this.scandataRecycleService.deleteScansPermanently(scans);
          return this.loadingService.loadFrom(deleteScans$, this);
        }),
      )
      .subscribe(({ failed }) => {
        if (failed.length === 0) return;

        this.showDeleteErrors(failed);
      });
  }

  private subscribeToLoadRecycledScans(): void {
    const nothing = '';

    this.loadScans$
      .pipe(
        startWith(nothing), // Initiate first load and then listen for loadScans$ to reload
        tap(() => this.loadError.set(false)),
        switchMap(() =>
          this.loadingService.loadFrom(this.scandataRecycleService.loadRecycledScans(), this).pipe(
            catchError(() => {
              this.loadError.set(true);
              return of([]);
            }),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private showDeleteConfirmation(scandataModels: ScandataModel[]): Observable<ScandataModel[]> {
    const title = this.translationService.translate(
      this.translations.confirmDeleteDialog.title.text,
      {
        'Reality Capture': 'Reality Capture',
      },
    );

    const pluralArgs: PluralArgs = { count: scandataModels.length };
    const pluralMap = this.translations.confirmDeleteDialog.message;
    const message = this.translationService.translatePlural(pluralArgs, pluralMap);

    const dialogData = new DialogData(
      title,
      message,
      { text: this.translations.confirmDeleteDialog.delete.text, color: 'danger' },
      CANCEL_BUTTON,
    );

    this.confirmDeleteDialogRef = this.dialogService.showMessage(dialogData);

    return this.confirmDeleteDialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed ?? false),
      map(() => scandataModels),
    );
  }

  private showDeleteErrors(failedModels: ScandataModel[]) {
    const names = `\n${failedModels.map((model) => model.name).join('\n')}`;
    const pluralArgs: PluralArgs = { count: failedModels.length, names };
    const pluralMap = this.translations.deleteErrorDialog.message;
    const message = this.translationService.translatePlural(pluralArgs, pluralMap);

    const dialogData = new DialogData(this.translations.deleteErrorDialog.title.text, message, {
      text: this.translations.deleteErrorDialog.dismiss.text,
      color: 'danger',
    });

    this.deleteErrorDialogRef = this.dialogService.showMessage(dialogData);
  }

  private showRestoreErrors(failedModels: ScandataModel[]) {
    const names = `\n${failedModels.map((model) => model.name).join('\n')}`;
    const pluralArgs: PluralArgs = { count: failedModels.length, names };
    const pluralMap = this.translations.restoreErrorDialog.message;
    const message = this.translationService.translatePlural(pluralArgs, pluralMap);

    const dialogData = new DialogData(this.translations.restoreErrorDialog.title.text, message, {
      text: this.translations.restoreErrorDialog.dismiss.text,
      color: 'danger',
    });

    this.restoreErrorDialogRef = this.dialogService.showMessage(dialogData);
  }
}
